import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { FONT_FAMILY_SERIF } from "../../theme";

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const isActive = (path: string) => location.pathname === path;

  const navLinks = [
    { label: "Dashboard", path: "/admin" },
    { label: "Galleries", path: "/admin/galleries" },
    { label: "Images", path: "/admin/images" },
    { label: "Contact", path: "/admin/contact-submissions" },
    { label: "Settings", path: "/admin/settings" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      {/* Admin Header */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: "rgba(250, 250, 250, 0.95)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          borderBottom: (theme) => `1px solid ${theme.custom.overlays.light}`,
          transition: "background-color 0.3s ease, backdrop-filter 0.3s ease",
        }}
      >
        <Toolbar
          sx={{
            height: "80px",
            px: { xs: "20px", md: "60px" },
            justifyContent: "space-between",
          }}
        >
          {/* Logo */}
          <Typography
            component={Link}
            to="/admin"
            sx={{
              fontFamily: FONT_FAMILY_SERIF,
              fontSize: "1.5rem", // 24px
              fontWeight: 300,
              color: "text.primary",
              textDecoration: "none",
              letterSpacing: "0.02em",
              transition: "opacity 0.2s ease",
              "&:hover": {
                opacity: 0.7,
              },
            }}
          >
            MB Admin
          </Typography>

          {/* Navigation */}
          <Box
            component="nav"
            sx={{
              display: { xs: "none", md: "flex" },
              gap: { xs: "16px", md: "40px" },
              alignItems: "center",
              flex: 1,
              justifyContent: "center",
              flexWrap: { xs: "wrap", md: "nowrap" },
            }}
          >
            {navLinks.map((link) => (
              <Typography
                key={link.path}
                component={Link}
                to={link.path}
                sx={{
                  fontSize: "0.875rem", // 14px
                  fontWeight: 400,
                  letterSpacing: "0.02em",
                  color: isActive(link.path)
                    ? "text.primary"
                    : "text.secondary",
                  textDecoration: "none",
                  position: "relative",
                  transition: "color 0.2s ease",
                  "&:hover": {
                    color: "text.primary",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: "-4px",
                    left: 0,
                    width: isActive(link.path) ? "100%" : "0%",
                    height: "1px",
                    backgroundColor: "text.primary",
                    transition: "width 0.3s ease",
                  },
                  "&:hover::after": {
                    width: "100%",
                  },
                }}
              >
                {link.label}
              </Typography>
            ))}
          </Box>

          {/* User Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: "12px", md: "24px" },
              flexWrap: { xs: "nowrap", md: "nowrap" },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.7rem", md: "0.75rem" },
                display: { xs: "none", sm: "block" }, // Hide email on very small screens
                maxWidth: { xs: "100px", md: "none" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user?.email}
            </Typography>
            <Typography
              component={Link}
              to="/"
              sx={{
                fontSize: { xs: "0.7rem", md: "0.75rem" },
                fontWeight: 400,
                letterSpacing: "0.02em",
                color: "text.secondary",
                textDecoration: "none",
                position: "relative",
                transition: "color 0.2s ease",
                whiteSpace: "nowrap",
                "&:hover": {
                  color: "text.primary",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-2px",
                  left: 0,
                  width: "0%",
                  height: "1px",
                  backgroundColor: "text.primary",
                  transition: "width 0.3s ease",
                },
                "&:hover::after": {
                  width: "100%",
                },
              }}
            >
              View Site
            </Typography>
            <Typography
              component="button"
              onClick={handleLogout}
              sx={{
                fontSize: { xs: "0.7rem", md: "0.75rem" },
                fontWeight: 400,
                letterSpacing: "0.02em",
                color: "text.secondary",
                textDecoration: "none",
                position: "relative",
                transition: "color 0.2s ease",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: { xs: "8px 4px", md: 0 }, // Better touch target on mobile
                minHeight: { xs: "36px", md: "auto" },
                whiteSpace: "nowrap",
                "&:hover": {
                  color: "text.primary",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-2px",
                  left: 0,
                  width: "0%",
                  height: "1px",
                  backgroundColor: "text.primary",
                  transition: "width 0.3s ease",
                },
                "&:hover::after": {
                  width: "100%",
                },
              }}
            >
              Logout
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ pt: "80px" }}>
        {" "}
        {/* Explicit 80px to match header height */}
        <Container maxWidth="xl" sx={{ py: 4 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
};
